'use client';

/**
 * Pluralsight-style topic grid — 3-column grid of topic pill rows with
 * brand-tinted icon tile + label + arrow.
 */
import { useTranslation } from 'react-i18next';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList } from '../_shared/coerce';
import { sectionTitle, cardReset } from '../_shared/styles';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

/** The `t` returned by {@code useTranslation} — typed structurally so this
 *  package needs no direct dependency on i18next's own types. */
type Translate = (key: string) => string;

interface TopicItem {
  label?: Localized;
  href?: string;
  icon?: Localized;
}

export interface PluralsightTopicGridProps {
  title?: Localized;
  subtitle?: Localized;
  items?: TopicItem[];
  /** Label for the "browse all" link. Defaults to the catalogue wording. */
  browseAllLabel?: Localized;
  browseAllHref?: string;
}

/** Built per render so a language switch re-reads the catalogue. */
const buildDefaults = (t: Translate): TopicItem[] => [
  { icon: 'AI', label: t('grid.topics.ai'), href: '/lxp/catalog?topic=ai' },
  { icon: '☁', label: t('grid.topics.cloud'), href: '/lxp/catalog?topic=cloud' },
  { icon: '▦', label: t('grid.topics.data'), href: '/lxp/catalog?topic=data' },
  { icon: '</>', label: t('grid.topics.software'), href: '/lxp/catalog?topic=software' },
  { icon: '◆', label: t('grid.topics.security'), href: '/lxp/catalog?topic=security' },
  { icon: '⚙', label: t('grid.topics.itOps'), href: '/lxp/catalog?topic=it-ops' },
];

export function PluralsightTopicGrid({
  title,
  subtitle,
  items,
  browseAllLabel,
  browseAllHref = '/lxp/catalog',
}: PluralsightTopicGridProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const list = asList<TopicItem>(items);
  const topics = (list.length ? list : buildDefaults(t)).slice(0, 12);

  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'clamp(48px, 6vw, 84px) 24px',
        background: 'var(--color-neutral-50, #f7f7fb)',
      }}
    >
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'end',
            justifyContent: 'space-between',
            gap: 24,
            flexWrap: 'wrap',
            marginBottom: 26,
          }}
        >
          <div>
            <h2 {...part('title')} style={sectionTitle}>
              {tx(title) || t('grid.topics.title')}
            </h2>
            {subtitle && (
              <p
                {...part('subtitle')}
                style={{
                  margin: '12px 0 0',
                  color: 'var(--color-text-secondary, #5b6472)',
                  fontSize: 16,
                }}
              >
                {tx(subtitle)}
              </p>
            )}
          </div>
          <a
            {...part('action')}
            href={browseAllHref}
            style={{
              color: 'var(--color-brand-600, #d6007f)',
              fontWeight: 'var(--font-weight-medium, 500)',
              textDecoration: 'none',
            }}
          >
            {tx(browseAllLabel) || t('grid.topics.browseAll')} →
          </a>
        </div>

        <div
          {...part('list')}
          className="ps-topic-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 14,
          }}
        >
          {topics.map((topic: TopicItem, i: number) => (
            <Card asChild key={i}>
              <a
                {...part('item', i)}
                href={topic.href || '#'}
                style={{
                  ...cardReset,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 18,
                  minHeight: 76,
                  padding: '0 18px',
                  borderRadius: 12,
                  background: 'var(--color-surface-card, #fff)',
                  color: 'var(--color-text-primary, #111)',
                  textDecoration: 'none',
                  fontWeight: 'var(--font-weight-medium, 500)',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      display: 'grid',
                      placeItems: 'center',
                      background: 'var(--color-brand-500, #fff)',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 900,
                    }}
                  >
                    {tx(topic.icon) || '▣'}
                  </span>
                  {tx(topic.label)}
                </span>
                <span
                  aria-hidden
                  style={{
                    fontWeight: 'var(--font-weight-medium, 500)',
                    color: 'var(--color-brand-600, #d6007f)',
                  }}
                >
                  →
                </span>
              </a>
            </Card>
          ))}
        </div>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 900px) {
          .ps-topic-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 560px) {
          .ps-topic-grid { grid-template-columns: 1fr !important; }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
